import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { Payroll } from 'src/app/models/payroll.model';
import { User } from 'src/app/models/user.model';
import { ApiService, UtilsService } from 'src/app/services';
import { LongTask } from 'src/app/models/long_task.model';
import { LongTaskPusherSerialized } from 'src/app/models/long_task_pusher_serialized.model';
import { PusherService } from 'src/app/services/pusher.service';
import { LongTaskStatus } from 'src/app/enums/long-task-status.enum';
import dayjs from 'dayjs';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDateRangePicker } from '@angular/material/datepicker';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatFormField, MatLabel, MatPrefix, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { NgClass, DecimalPipe, PercentPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import {
  PayrollsCommentsDialogComponent,
  PayrollsCommentsDialogData,
  PayrollsCommentsDialogResult,
} from 'src/app/components/main/employees/rrhh/payrolls/payrolls-comments-dialog/payrolls-comments-dialog.component';
import {
  PayrollPlusesJustificationDialogComponent,
  PayrollPlusesJustificationDialogData,
} from 'src/app/components/main/shared/payroll-pluses-justification-dialog/payroll-pluses-justification-dialog.component';
import { PayrollPlusJustification } from 'src/app/models/payroll-plus-justification.model';
import { PayrollJustificationBreakdown } from 'src/app/models/payroll-justification-breakdown.model';
import { PayrollLine } from 'src/app/models/payroll-line.model';
import { PayrollConceptKind } from 'src/app/models/payroll-concept.model';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';
import {
  PAYROLL_CONCEPT_KIND,
  PAYROLL_CONCEPT_SLUG,
  bisualPlusPayrollLines,
  bisualPlusesTotal,
  estimateExtraHoursAmount,
  extraPayrollConceptLineChips,
  payrollLineAmountBySlug,
  payrollLineMetaBoolean,
  payrollLineMetaNumber,
} from 'src/app/models/payroll-line.utils';
import {
  PayrollsMileageDialogComponent,
  PayrollsMileageDialogData,
} from 'src/app/components/main/employees/rrhh/payrolls/payrolls-mileage-dialog/payrolls-mileage-dialog.component';
import {
  PayrollGestoriaImportReviewDialogComponent,
  PayrollGestoriaImportReviewDialogData,
} from 'src/app/components/main/employees/rrhh/payrolls/payroll-gestoria-import-review-dialog/payroll-gestoria-import-review-dialog.component';

interface FilterFormValue {
  working_period_from?: Date | null;
  working_period_to?: Date | null;
  [key: string]: any;
}

interface PayrollAdjustmentChip {
  key: string;
  emoji: string;
  label: string;
  /** Signed euro amount; null when the chip is a flag without money (e.g. absences). */
  amount: number | null;
  /** Optional secondary text when amount is null (e.g. "3 h"). */
  text?: string;
  kind?: PayrollConceptKind;
  is_expected_normal?: boolean;
  is_kpi_cut?: boolean;
}

@Component({
  selector: 'app-payrolls',
  templateUrl: './payrolls.component.html',
  styleUrls: ['./payrolls.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatButton,
    MatIcon,
    FormsModule,
    ReactiveFormsModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatIconButton,
    MatPrefix,
    MatTooltip,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatHint,
    MatSuffix,
    MatDateRangePicker,
    MatInput,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatCheckbox,
    CdkTextareaAutosize,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DecimalPipe,
    PercentPipe,
    CurrencyPipe,
    DatePipe,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    UserProfilePictureComponent,
  ],
})
export class PayrollsComponent extends IndexTemplateComponent implements OnDestroy, OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  PER_HOUR_CONTRACTS: string[] = ['per-hours', 'practice'];
  readonly CONCEPTS_TO_SHOW: number = 3;

  override pageSize: number = 50;
  override pageSizeOptions: number[] = [25, 50, 75, 100];
  isFormModified = false;

  total: number = 0;

  public templateDisplayedColumns: string[] = [
    'name',

    'number_of_payments_per_year',
    'sa',
    'sm_theorical',
    'sm_practical',
    'payroll_adjustments',
    'hours_worked_since_last_day_taken_into_account_for_hours',
    'last_day_taken_into_account_for_hours',
    // 'seniority_bonus',
    // 'irpf',

    'flexible_remuneration',
    // 'extract_flexible_remuneration_from_salary',

    // 'fuel_cost_per_km',
    // 'distance_in_km',
    // 'total_fuel_cost',

    'additional_salary',
    'additional_hours',

    'total_to_pay',

    'is_send_to_management',
    'is_send_to_employee',
    'is_modified_after_sent_to_employee',
    'gestoria_holded_status',
    'row_actions',
  ];

  public dataSource: MatTableDataSource<FormGroup> = new MatTableDataSource<FormGroup>();

  override filterForm: UntypedFormGroup;
  form: FormGroup;

  override serializer: { [key: string]: () => any } = {
    working_period_from: () => {
      return dayjs(this.filterForm.value.working_period_from).format('YYYY-MM-DD');
    },
    working_period_to: () => {
      return dayjs(this.filterForm.value.working_period_to).format('YYYY-MM-DD');
    },
  };

  override deserializer: { [key: string]: (data: string) => any } = {
    working_period_from: (data: string) => {
      return dayjs(data, 'YYYY-MM-DD').toDate();
    },
    working_period_to: (data: string) => {
      return dayjs(data, 'YYYY-MM-DD').toDate();
    },
  };

  is_send_management_all: boolean = false;
  is_send_employees_all: boolean = false;

  override filterFormExtraParams: any = {
    working_period_from: [
      this.activatedRoute.snapshot.queryParamMap.get('working_period_from') != null
        ? dayjs(this.activatedRoute.snapshot.queryParamMap.get('working_period_from'), 'YYYY-MM-DD').toDate()
        : dayjs().startOf('month').toDate(),
      Validators.compose([Validators.required]),
    ],
    working_period_to: [
      this.activatedRoute.snapshot.queryParamMap.get('working_period_to') != null
        ? dayjs(this.activatedRoute.snapshot.queryParamMap.get('working_period_to'), 'YYYY-MM-DD').toDate()
        : dayjs().endOf('month').toDate(),
      Validators.compose([Validators.required]),
    ],
  };

  private currentPusherChannel: string | null;
  generatingPayrolls: boolean = false;
  importing_gestoria_pdf: boolean = false;
  private gestoria_import_long_task_active = false;

  @ViewChild('gestoria_pdf_input') private gestoria_pdf_input?: ElementRef<HTMLInputElement>;
  private readonly destroy$ = new Subject<void>();

  private readonly api = inject(ApiService);
  private readonly pusher = inject(PusherService);
  private readonly snack = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly currencyPipe = inject(CurrencyPipe);

  protected override utils!: UtilsService;

  constructor() {
    super(inject(Router), inject(FormBuilder), inject(ActivatedRoute), inject(UtilsService));

    this.displayedColumns = { ...this.templateDisplayedColumns };
    this.form = this.fb.group({
      general_comment: [''],
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setCurrentPusherChannel();

    this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (prev: FilterFormValue, curr: FilterFormValue): boolean =>
            prev.working_period_from === curr.working_period_from && prev.working_period_to === curr.working_period_to
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((): void => {
        if (this.filterForm.value.working_period_from && this.filterForm.value.working_period_to) {
          this.setCurrentPusherChannel();
        }
      });
  }

  override fetchData() {
    this.displayedColumns = [...this.templateDisplayedColumns];

    const params = {
      ...this.filterForm.value,
      working_period_from: dayjs(this.filterForm.value.working_period_from).format('YYYY-MM-DD'),
      working_period_to: dayjs(this.filterForm.value.working_period_to).format('YYYY-MM-DD'),
    };

    this.is_send_management_all = true;
    this.is_send_employees_all = true;

    this.api
      .getPayrolls(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ArrayResponse<Payroll>) => {
          // const plusConceptColumns = [];

          // for (let i = 0; i < this.CONCEPTS_TO_SHOW; i++) {
          //   plusConceptColumns.push(`plus_${i}`);
          // }

          this.displayedColumns = [...this.templateDisplayedColumns]; // , ...plusConceptColumns];
          // this.displayedColumns.splice(4, 0, ...plusConceptColumns);

          this.dataSource = new MatTableDataSource<FormGroup>(
            data.data.map(p => {
              const lines = p.payroll_lines || [];
              const brute_base_salary = payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.BASE_SALARY);
              const seniority_bonus = payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.SENIORITY_BONUS);
              const flexible_remuneration = payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION);
              const additional_salary = payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY);
              const total_fuel_cost = payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE);
              const distance_in_km = payrollLineMetaNumber(lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, 'km');
              const fuel_cost_per_km = payrollLineMetaNumber(lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, 'rate');
              const additional_hours = payrollLineMetaNumber(lines, PAYROLL_CONCEPT_SLUG.EXTRA_HOURS, 'hours');
              const extract_flexible_remuneration_from_salary = payrollLineMetaBoolean(
                lines,
                PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
                'extracted_from_salary'
              );

              const fg = this.fb.group({
                id: [p.id, Validators.compose([Validators.required])],
                user_id: [p.user_id, Validators.compose([Validators.required])],

                brute_base_salary: [brute_base_salary, Validators.compose([Validators.required])],
                brute_base_anual_salary: [p.brute_base_anual_salary, Validators.compose([Validators.required])],
                seniority_bonus: [seniority_bonus, Validators.compose([])],
                seniority_bonus_anual: [p.seniority_bonus_anual, Validators.compose([])],
                irpf_prcnt: [p.irpf_prcnt, Validators.compose([Validators.required])],
                ss_prcnt: [p.ss_prcnt, Validators.compose([Validators.required])],
                price_per_hour: [p.price_per_hour, Validators.compose([Validators.min(0)])],
                hours_worked_since_last_day_taken_into_account_for_hours: [
                  p.hours_worked_since_last_day_taken_into_account_for_hours,
                  Validators.compose([Validators.min(0)]),
                ],
                last_day_taken_into_account_for_hours: [p.last_day_taken_into_account_for_hours],

                extract_flexible_remuneration_from_salary: [
                  extract_flexible_remuneration_from_salary,
                  Validators.compose([Validators.required]),
                ],
                flexible_remuneration: [
                  flexible_remuneration,
                  Validators.compose([Validators.required, Validators.min(0), Validators.max(220)]),
                ],

                distance_in_km: [distance_in_km, Validators.compose([Validators.required])],
                fuel_cost_per_km: [fuel_cost_per_km, Validators.compose([Validators.required])],
                total_fuel_cost: [total_fuel_cost, Validators.compose([Validators.required])],
                has_mileage_trips: [Number.isFinite(distance_in_km) && distance_in_km > 0],

                additional_salary: [additional_salary, Validators.compose([Validators.required])],
                additional_hours: [additional_hours, Validators.compose([Validators.required])],

                comments: [p.comments ?? '', Validators.compose([])],
                general_comment: [p.general_comment, Validators.compose([Validators.required])],
                comment_for_employee: [p.comment_for_employee ?? '', Validators.compose([])],

                payroll_file: ['', Validators.compose([Validators.required])],
                payroll_file_path: [p.payroll_file_path, Validators.compose([Validators.required])],
                is_send_to_employee: [p.is_send_to_employee, Validators.compose([Validators.required])],
                is_send_to_management: [p.is_send_to_management, Validators.compose([Validators.required])],
                is_modified_after_sent_to_employee: [p.is_modified_after_sent_to_employee, Validators.compose([Validators.required])],
                gestoria_holded_status: [p.latest_accounting_draft?.status ?? null],
                total_to_pay: [p.latest_accounting_draft?.total_to_pay ?? null],

                working_period_from: [p.working_period_from, Validators.compose([Validators.required])],
                working_period_to: [p.working_period_to, Validators.compose([Validators.required])],
                user_full_name: ['', Validators.compose([Validators.required])],

                payroll_lines: [lines],
                plus_justifications: [p.plus_justifications || null],

                // extres
                user: [p.user],
              }) as any;

              const plusLines = bisualPlusPayrollLines(lines);

              for (let i = 0; i < this.CONCEPTS_TO_SHOW; i++) {
                if (plusLines[i]) {
                  const line = plusLines[i];
                  fg.addControl(`plus_${i}_name`, this.fb.control(line.lineable?.name ?? ''));
                  fg.addControl(`plus_${i}_amount`, this.fb.control(line.amount || 0));
                } else {
                  fg.addControl(`plus_${i}_name`, this.fb.control(''));
                  fg.addControl(`plus_${i}_amount`, this.fb.control(0));
                }
              }

              this.is_send_management_all = this.is_send_management_all && p.is_send_to_management;
              this.is_send_employees_all = this.is_send_employees_all && p.is_send_to_employee && p.payroll_file_path != null;

              fg.controls['extract_flexible_remuneration_from_salary'].disable();

              fg.valueChanges.subscribe(() => {
                this.isFormModified = true;
                this.recalcTotal();
              });

              fg.controls['distance_in_km'].valueChanges.subscribe((val: number) => {
                if (
                  val != null &&
                  !isNaN(val) &&
                  fg.controls['fuel_cost_per_km'].value != null &&
                  !isNaN(fg.controls['fuel_cost_per_km'].value)
                ) {
                  fg.controls['total_fuel_cost'].setValue(val * fg.controls['fuel_cost_per_km'].value);
                }

                fg.controls['has_mileage_trips'].setValue(Number.isFinite(Number(val)) && Number(val) > 0, { emitEvent: false });
              });

              fg.controls['fuel_cost_per_km'].valueChanges.subscribe((val: number) => {
                if (
                  val != null &&
                  !isNaN(val) &&
                  fg.controls['distance_in_km'].value != null &&
                  !isNaN(fg.controls['distance_in_km'].value)
                ) {
                  fg.controls['total_fuel_cost'].setValue(val * fg.controls['distance_in_km'].value);
                }
              });

              return fg;
            })
          );

          this.setMetadata(+data.meta.total, data.meta.current_page - 1, data.meta.per_page);

          this.recalcTotal();

          this.listenForm();

          if (data?.data?.[0]?.general_comment) {
            this.form.controls['general_comment'].patchValue(data.data[0].general_comment, { emitEvent: false });
          }
        },
      });
  }

  onPayrollPdfFileSelected(event: Event, idx: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length === 1) {
      this.dataSource.data[idx].controls['payroll_file'].setValue(input.files[0]);
    }
    input.value = '';
  }

  removeFile(index: number): void {
    const fg = this.dataSource.data[index];
    fg.controls['payroll_file_path'].setValue(null);
    fg.controls['payroll_file'].setValue('');
  }

  openPayrollCommentsDialog(index: number): void {
    const fg = this.dataSource.data[index];
    const user = fg.value.user as User;
    const periodLabel = ((): string => {
      const period_from = dayjs(fg.value.working_period_from);
      const period_to = dayjs(fg.value.working_period_to);
      const period_ends_on_same_month = period_from.isSame(period_to, 'month');

      return period_ends_on_same_month
        ? period_from.format('MM/YYYY')
        : `del ${period_from.format('MM/YYYY')} al ${period_to.format('MM/YYYY')}`;
    })();

    const title = `Comentarios sobre la nómina de ${user.name} ${user.surnames} - ${periodLabel}`;

    this.dialog
      .open<PayrollsCommentsDialogComponent, PayrollsCommentsDialogData, PayrollsCommentsDialogResult | undefined>(
        PayrollsCommentsDialogComponent,
        {
          width: '560px',
          maxWidth: '95vw',
          data: {
            title,
            comments: (fg.controls['comments'].value as string) ?? '',
            comment_for_employee: (fg.controls['comment_for_employee'].value as string) ?? '',
          },
        }
      )
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: PayrollsCommentsDialogResult | undefined): void => {
        if (result === undefined) {
          return;
        }

        fg.controls['comments'].setValue(result.comments);
        fg.controls['comment_for_employee'].setValue(result.comment_for_employee);
      });
  }

  openPayrollJustificationDialog(index: number): void {
    const fg = this.dataSource.data[index];
    const user = fg.value.user as User;
    const period_from = dayjs(fg.value.working_period_from);
    const period_to = dayjs(fg.value.working_period_to);
    const period_label = period_from.isSame(period_to, 'month')
      ? period_from.format('MM/YYYY')
      : `del ${period_from.format('MM/YYYY')} al ${period_to.format('MM/YYYY')}`;

    this.dialog.open<PayrollPlusesJustificationDialogComponent, PayrollPlusesJustificationDialogData>(
      PayrollPlusesJustificationDialogComponent,
      {
        width: '680px',
        maxWidth: '95vw',
        data: {
          title: `Justificación de nómina · ${user.name} ${user.surnames}`,
          period_label,
          justification: (fg.value.plus_justifications as PayrollPlusJustification | null) ?? null,
          breakdown: this.buildPayrollJustificationBreakdown(fg),
          payroll_id: fg.value.id as number,
          can_view_mileage_detail: true,
        },
      }
    );
  }

  buildPayrollJustificationBreakdown(formGroup: UntypedFormGroup): PayrollJustificationBreakdown {
    const lines = (formGroup.value.payroll_lines as PayrollLine[] | null) ?? [];
    const form_pluses_total =
      Number(formGroup.value.plus_0_amount || 0) + Number(formGroup.value.plus_1_amount || 0) + Number(formGroup.value.plus_2_amount || 0);
    const pluses_total = form_pluses_total > 0 ? form_pluses_total : bisualPlusesTotal(lines);
    const brute_base_salary = payrollLineAmountBySlug(
      lines,
      PAYROLL_CONCEPT_SLUG.BASE_SALARY,
      Number(formGroup.value.brute_base_salary || 0)
    );
    const seniority_bonus = payrollLineAmountBySlug(
      lines,
      PAYROLL_CONCEPT_SLUG.SENIORITY_BONUS,
      Number(formGroup.value.seniority_bonus || 0)
    );
    const distance_in_km = Number(formGroup.value.distance_in_km || 0);
    const fuel_cost_per_km = Number(formGroup.value.fuel_cost_per_km || 0);
    const additional_hours = Number(formGroup.value.additional_hours || 0);
    const total_fuel_cost =
      Number(formGroup.value.total_fuel_cost || 0) ||
      payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE, Math.round(distance_in_km * fuel_cost_per_km * 100) / 100);
    const flexible_remuneration = payrollLineAmountBySlug(
      lines,
      PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
      Number(formGroup.value.flexible_remuneration || 0)
    );
    const additional_salary = payrollLineAmountBySlug(
      lines,
      PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY,
      Number(formGroup.value.additional_salary || 0)
    );
    const additional_hours_amount = payrollLineAmountBySlug(
      lines,
      PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
      estimateExtraHoursAmount(additional_hours, brute_base_salary)
    );

    return {
      brute_base_salary,
      seniority_bonus,
      pluses_total,
      flexible_remuneration,
      extract_flexible_remuneration_from_salary: !!formGroup.value.extract_flexible_remuneration_from_salary,
      distance_in_km,
      fuel_cost_per_km,
      total_fuel_cost,
      additional_salary,
      additional_hours,
      additional_hours_amount,
      sm_calculated: brute_base_salary + seniority_bonus + pluses_total,
      comments: (formGroup.value.comments as string | null) ?? null,
      comment_for_employee: (formGroup.value.comment_for_employee as string | null) ?? null,
    };
  }

  estimateAdditionalHoursAmount(additional_hours: number, brute_base_salary: number): number {
    return estimateExtraHoursAmount(additional_hours, brute_base_salary);
  }

  getPayrollAdjustmentChips(formGroup: UntypedFormGroup): PayrollAdjustmentChip[] {
    const chips: PayrollAdjustmentChip[] = [];
    const breakdown = this.buildPayrollJustificationBreakdown(formGroup);
    const justification = (formGroup.value.plus_justifications as PayrollPlusJustification | null) ?? null;

    const bisual_plus_original_amount = justification?.bisual_plus_original_amount;
    const plus_after_kpi = justification?.bisual_plus_amount_after_kpi;
    const plus_kpi_cut =
      bisual_plus_original_amount != null && plus_after_kpi != null && bisual_plus_original_amount > plus_after_kpi
        ? Math.round((bisual_plus_original_amount - plus_after_kpi) * 100) / 100
        : 0;

    if (breakdown.pluses_total > 0) {
      chips.push({
        key: 'plus',
        emoji: '🎖️',
        label: plus_kpi_cut > 0 ? 'Plus Bisual (KPI aplicado)' : 'Plus Bisual',
        amount: breakdown.pluses_total,
        text: plus_kpi_cut > 0 ? `KPI −${this.currencyPipe.transform(plus_kpi_cut)}` : undefined,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
        is_expected_normal: true,
        is_kpi_cut: plus_kpi_cut > 0,
      });
    }

    // System concept lines (and edited form mirrors of those lines).
    if (breakdown.total_fuel_cost > 0) {
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE,
        emoji: '⛽',
        label: 'Gasolina / kilometraje',
        amount: breakdown.total_fuel_cost,
        kind: PAYROLL_CONCEPT_KIND.EXPENSE,
      });
    }

    if (breakdown.additional_hours > 0 || breakdown.additional_salary > 0 || breakdown.additional_hours_amount > 0) {
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
        emoji: '⏱️',
        label: 'Horas / importe extra',
        amount:
          breakdown.additional_hours_amount > 0
            ? breakdown.additional_hours_amount
            : breakdown.additional_salary > 0
              ? breakdown.additional_salary
              : null,
        text: breakdown.additional_hours > 0 ? `${breakdown.additional_hours} h` : undefined,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
      });
    }

    if (breakdown.flexible_remuneration > 0 && !breakdown.extract_flexible_remuneration_from_salary) {
      chips.push({
        key: PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
        emoji: '🍽️',
        label: 'Ret. flexible (incluida)',
        amount: breakdown.flexible_remuneration,
        kind: PAYROLL_CONCEPT_KIND.SALARY,
      });
    }

    const lines = (formGroup.value.payroll_lines as PayrollLine[] | null) ?? [];
    for (const chip of extraPayrollConceptLineChips(lines, [
      PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE,
      PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
      PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
      PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY,
    ])) {
      chips.push(chip);
    }

    if (breakdown.comments?.trim()) {
      chips.push({
        key: 'absence',
        emoji: '🪑',
        label: 'Ausencias / notas internas',
        amount: null,
        text: '···',
      });
    }

    return chips;
  }

  formatAdjustmentAmount(amount: number, withSign = true): string {
    const absolute = this.currencyPipe.transform(Math.abs(amount)) ?? `${Math.abs(amount)}`;
    if (!withSign) {
      return absolute;
    }
    if (amount > 0) {
      return `+${absolute}`;
    }
    if (amount < 0) {
      return `−${absolute}`;
    }
    return absolute;
  }

  openPayrollMileageDialog(index: number): void {
    const fg = this.dataSource.data[index];
    const user = fg.value.user as User;
    const period_from = dayjs(fg.value.working_period_from);
    const period_to = dayjs(fg.value.working_period_to);
    const period_ends_on_same_month = period_from.isSame(period_to, 'month');
    const periodLabel = period_ends_on_same_month
      ? period_from.format('MM/YYYY')
      : `del ${period_from.format('MM/YYYY')} al ${period_to.format('MM/YYYY')}`;

    this.dialog.open<PayrollsMileageDialogComponent, PayrollsMileageDialogData>(PayrollsMileageDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        title: `Kilometraje de ${user.name} ${user.surnames} - ${periodLabel}`,
        payroll_id: fg.value.id as number,
      },
    });
  }

  savePayrolls() {
    const payload: Payroll[] = this.dataSource.data.map(fg => {
      const val: any = fg.value;
      delete val.user;
      delete val.is_modified_after_sent_to_employee;
      delete val.has_mileage_trips;
      delete val.gestoria_holded_status;
      val.general_comment = this.form.value.general_comment;
      return val as Payroll;
    });

    this.api
      .createPayrollsBulk(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.isFormModified = false;
        },
        error: error => {
          this.isFormModified = true;
        },
        complete: () => {
          this.fetchData();
        },
      });
  }

  sendPayrollsToTheManagement() {
    if (confirm('¿Estás seguro que quieres enviar un mail a la gestoría?')) {
      this.api
        .sendPayrollsToTheManagement(this.filterForm.value.working_period_from, this.filterForm.value.working_period_to)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          complete: () => {
            this.fetchData();
          },
        });
    }
  }

  sendPayrollsToTheEmployee() {
    if (confirm('¿Estás seguro que quieres enviar una notificación a los empleados conforme ya tienen la nómina lista?')) {
      this.api
        .sendPayrollsToTheEmployee(this.filterForm.value.working_period_from, this.filterForm.value.working_period_to)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          complete: () => {
            this.fetchData();
          },
        });
    }
  }

  goPreviousMonth() {
    const prev_month: dayjs.Dayjs = dayjs(this.filterForm.value.working_period_from).subtract(1, 'month');
    this.filterForm.patchValue({
      working_period_from: prev_month.clone().startOf('month').toDate(),
      working_period_to: prev_month.clone().endOf('month').toDate(),
      page: 1,
    });
  }

  goNextMonth() {
    const next_month: dayjs.Dayjs = dayjs(this.filterForm.value.working_period_from).add(1, 'month');
    this.filterForm.patchValue({
      working_period_from: next_month.clone().startOf('month').toDate(),
      working_period_to: next_month.clone().endOf('month').toDate(),
      page: 1,
    });
  }

  generatePayrolls(regenerate: boolean = false): void {
    const confirmMessage = regenerate
      ? 'Se regenerarán las nóminas no enviadas a gestoría y se crearán las que falten. Las ya enviadas se mantendrán. ¿Continuar?'
      : '¿Estás seguro que quieres generar las nóminas de este periodo?';

    if (!confirm(confirmMessage)) return;

    this.generatingPayrolls = true;
    this.gestoria_import_long_task_active = false;

    const params = this.getLongTasksParams();

    this.api
      .getLongTasks(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ArrayResponse<LongTask>) => {
          const alreadyGeneratingPayrollsForThisDate = data.data && data.data.length > 0;

          if (alreadyGeneratingPayrollsForThisDate) {
            this.snack.open(
              'Ya hay una generación de nóminas en proceso para esta fecha. Por favor, espera a que termine antes de generar nuevas nóminas.',
              'Ok',
              { duration: 5000 }
            );
            this.cleanupPusherSubscription();
            return;
          }

          this.api
            .preparePayrolls(dayjs(this.filterForm.value.working_period_from), dayjs(this.filterForm.value.working_period_to), regenerate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (longTask: LongTask) => {
                if (longTask.error_message) {
                  this.snack.open(`Error al generar las nóminas: ${longTask.error_message}`, 'Ok', { duration: 5000 });
                  this.cleanupPusherSubscription();
                  return;
                }

                this.cleanupPusherSubscription(false);
                const channelName = longTask.pusher_channel_to_check;

                if (this.currentPusherChannel === channelName) {
                  return;
                }

                this.currentPusherChannel = channelName;
                this.generatingPayrolls = true;

                this.pusher.subscribeToLongTaskChannel(this.currentPusherChannel, (data: LongTaskPusherSerialized): void =>
                  this.handleLongTaskUpdate(data)
                );
              },
              error: () => {
                this.cleanupPusherSubscription();
                this.snack.open('Error al generar las nóminas', 'Ok', { duration: 3000 });
                this.generatingPayrolls = false;
              },
            });
        },
      });
  }

  private getLongTasksParams(): {
    status: LongTaskStatus;
    type: string;
    [key: string]: string | LongTaskStatus;
  } {
    return {
      status: LongTaskStatus.Processing,
      type: 'App\\Jobs\\GeneratePayrollsJob',
      'data[date]': dayjs(this.filterForm.value.working_period_from).format('YYYY-MM-DD'),
    };
  }

  private setCurrentPusherChannel(): void {
    const params = this.getLongTasksParams();

    this.api
      .getLongTasks(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ArrayResponse<LongTask>) => {
          const alreadyGeneratingPayrollsForThisDate = data.data && data.data.length > 0;

          if (alreadyGeneratingPayrollsForThisDate) {
            const longTask = data.data[0];
            const channelName = longTask.pusher_channel_to_check;

            if (this.currentPusherChannel === channelName) {
              return;
            }

            this.cleanupPusherSubscription(false);

            this.currentPusherChannel = channelName;
            this.generatingPayrolls = true;

            this.pusher.subscribeToLongTaskChannel(this.currentPusherChannel, (data: LongTaskPusherSerialized): void =>
              this.handleLongTaskUpdate(data)
            );
          } else {
            this.cleanupPusherSubscription(true);
          }
        },
        error: () => {
          this.cleanupPusherSubscription(true);
        },
      });
  }

  private handleLongTaskUpdate(data: LongTaskPusherSerialized): void {
    if (data.status === LongTaskStatus.Completed) {
      const longTaskId = this.extractLongTaskIdFromUrl(data.get_result_url);

      if (longTaskId) {
        if (this.gestoria_import_long_task_active) {
          this.fetchGestoriaImportResult(longTaskId);
        } else {
          this.fetchPayrollsGenerationResult(longTaskId);
        }
      } else {
        console.error('Could not extract long task ID from URL:', data.get_result_url);
        this.cleanupPusherSubscription();
        this.fetchData();
      }
    } else if (data.status === LongTaskStatus.Failed) {
      this.cleanupPusherSubscription();
      if (this.gestoria_import_long_task_active) {
        this.importing_gestoria_pdf = false;
        this.gestoria_import_long_task_active = false;
        this.snack.open(data.error_message || 'Error al importar el PDF de gestoría', 'Ok', { duration: 5000 });
      }
    }
  }

  pickGestoriaPdf(): void {
    this.gestoria_pdf_input?.nativeElement.click();
  }

  onGestoriaPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    this.importing_gestoria_pdf = true;
    this.gestoria_import_long_task_active = true;

    const from = dayjs(this.filterForm.value.working_period_from);
    const to = dayjs(this.filterForm.value.working_period_to);

    this.api
      .getLongTasks({
        status: LongTaskStatus.Processing,
        type: 'App\\Jobs\\ImportPayrollGestoriaPdfJob',
        'data[date]': from.format('YYYY-MM-DD'),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: ArrayResponse<LongTask>) => {
          if (data.data?.length) {
            this.importing_gestoria_pdf = false;
            this.gestoria_import_long_task_active = false;
            this.snack.open('Ya hay una importación de gestoría en curso para este periodo.', 'Ok', { duration: 5000 });
            return;
          }

          this.api
            .importPayrollGestoriaPdf(file, from, to)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (longTask: LongTask) => {
                // Sync queue finishes before we can subscribe to Pusher.
                if (longTask.status === LongTaskStatus.Completed) {
                  this.fetchGestoriaImportResult(longTask.id);
                  return;
                }

                if (longTask.status === LongTaskStatus.Failed) {
                  this.importing_gestoria_pdf = false;
                  this.gestoria_import_long_task_active = false;
                  this.snack.open(longTask.error_message || 'Error al importar el PDF de gestoría', 'Ok', {
                    duration: 5000,
                  });
                  return;
                }

                this.cleanupPusherSubscription(false);
                this.currentPusherChannel = longTask.pusher_channel_to_check;
                this.pusher.subscribeToLongTaskChannel(this.currentPusherChannel, (payload: LongTaskPusherSerialized): void =>
                  this.handleLongTaskUpdate(payload)
                );
              },
              error: () => {
                this.importing_gestoria_pdf = false;
                this.gestoria_import_long_task_active = false;
                this.snack.open('No se pudo iniciar la importación del PDF', 'Ok', { duration: 4000 });
              },
            });
        },
        error: () => {
          this.importing_gestoria_pdf = false;
          this.gestoria_import_long_task_active = false;
        },
      });
  }

  openGestoriaReview(): void {
    const from = dayjs(this.filterForm.value.working_period_from);
    const to = dayjs(this.filterForm.value.working_period_to);

    this.api
      .rematchPayrollGestoriaImport(from, to)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: importData => {
          if (!importData?.drafts?.length) {
            this.snack.open('No hay una importación lista para revisar en este periodo.', 'Ok', { duration: 4000 });
            return;
          }

          const payrolls: Payroll[] = this.dataSource.data.map(fg => fg.value as Payroll);
          this.dialog
            .open(PayrollGestoriaImportReviewDialogComponent, {
              width: '96vw',
              maxWidth: '1280px',
              maxHeight: '90vh',
              panelClass: 'payroll-gestoria-review-dialog-panel',
              autoFocus: 'dialog',
              data: {
                import: importData,
                payrolls,
              } satisfies PayrollGestoriaImportReviewDialogData,
            })
            .afterClosed()
            .subscribe(() => {
              this.fetchData();
            });
        },
        error: () => this.snack.open('No se pudo cargar la importación', 'Ok', { duration: 3000 }),
      });
  }

  gestoriaHoldedIcon(status: string | null): string {
    switch (status) {
      case 'pending_review':
        return 'warning';
      case 'confirmed':
        return 'hourglass_top';
      case 'synced':
        return 'check_circle';
      case 'failed':
        return 'cancel';
      default:
        return '';
    }
  }

  gestoriaHoldedIconClass(status: string | null): string {
    switch (status) {
      case 'pending_review':
        return 'text-warning';
      case 'confirmed':
        return 'text-warning';
      case 'synced':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      default:
        return '';
    }
  }

  gestoriaHoldedTooltip(status: string | null): string {
    switch (status) {
      case 'pending_review':
        return 'Emparejada con PDF de gestoría — pendiente de revisión';
      case 'confirmed':
        return 'Importación confirmada — pendiente de sincronizar con Holded';
      case 'synced':
        return 'Nómina contable sincronizada con Holded';
      case 'failed':
        return 'Error al sincronizar con Holded';
      default:
        return '';
    }
  }

  private fetchGestoriaImportResult(longTaskId: number): void {
    this.api
      .getLongTaskResult(longTaskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: { drafts_count?: number; updated_from_pdf?: number; carried_forward?: number }) => {
          this.cleanupPusherSubscription();
          this.importing_gestoria_pdf = false;
          this.gestoria_import_long_task_active = false;
          const updated = data.updated_from_pdf ?? data.drafts_count ?? 0;
          const carried = data.carried_forward ?? 0;
          const message =
            carried > 0
              ? `PDF procesado: ${updated} actualizada(s) desde el PDF, ${carried} conservada(s) del import anterior.`
              : `PDF de gestoría procesado (${updated} nómina(s)). Revisa la importación.`;
          this.snack.open(message, 'Ok', { duration: 5000 });
          this.fetchData();
          this.openGestoriaReview();
        },
        error: () => {
          this.cleanupPusherSubscription();
          this.importing_gestoria_pdf = false;
          this.gestoria_import_long_task_active = false;
          this.snack.open('Error al obtener el resultado de la importación', 'Ok', { duration: 4000 });
        },
      });
  }

  private extractLongTaskIdFromUrl(url: string): number {
    // /api/v1/long-tasks/123/result
    const match = url.match(/\/long-tasks\/(\d+)\/result/);
    return match ? parseInt(match[1], 10) : 0;
  }

  private fetchPayrollsGenerationResult(longTaskId: number): void {
    this.api
      .getLongTaskResult(longTaskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: { generated_count?: number; regenerated_count?: number; skipped_sent_count?: number }) => {
          const generated = data.generated_count || 0;
          const regenerated = data.regenerated_count || 0;
          const skipped = data.skipped_sent_count || 0;
          const parts = [`${generated} generadas`, `${regenerated} regeneradas`];
          if (skipped > 0) {
            parts.push(`${skipped} omitidas (ya enviadas a gestoría)`);
          }
          const message = `Nóminas: ${parts.join(', ')}.`;
          this.cleanupPusherSubscription();
          this.fetchData();
          this.snack.open(message, 'Ok', { duration: 5000 });
        },
      });
  }

  private cleanupPusherSubscription(shouldResetGeneratingFlag: boolean = true): void {
    if (this.currentPusherChannel) {
      this.pusher.unsubscribeFromChannel(this.currentPusherChannel);
      this.currentPusherChannel = null;
    }

    if (shouldResetGeneratingFlag) {
      this.generatingPayrolls = false;
      this.importing_gestoria_pdf = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupPusherSubscription();
  }

  downloadPdf(idx: number) {
    this.api.downloadPayrollPdf(this.dataSource.data[idx].value.id).subscribe(data => {
      this.utils.downloadPdfFromBlobResponse(data);
    });
  }

  downloadPayrollsFile() {
    const body = {
      from: dayjs(this.filterForm.value.working_period_from).format('YYYY-MM-DD'),
      to: dayjs(this.filterForm.value.working_period_to).format('YYYY-MM-DD'),
    };

    this.api.downloadBulkPayrollsXlsx(body).subscribe(data => {
      this.utils.downloadXlsxFromBlobResponse(data);
    });
  }

  private recalcTotal() {
    let sum: number = 0;

    this.dataSource.data.forEach(fg => {
      const lines = (fg.value.payroll_lines as PayrollLine[] | null) ?? [];
      let sumItem: number = 0;
      sumItem += Number(fg.value.brute_base_salary || 0);
      sumItem += Number(fg.value.seniority_bonus || 0);
      sumItem += Number(fg.value.price_per_hour || 0) * Number(fg.value.hours_worked_since_last_day_taken_into_account_for_hours || 0);

      const flexible_remuneration = payrollLineAmountBySlug(
        lines,
        PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
        Number(fg.value.flexible_remuneration || 0)
      );
      if (!fg.value.extract_flexible_remuneration_from_salary) {
        sumItem += flexible_remuneration;
      }

      sumItem +=
        Number(fg.value.total_fuel_cost || 0) ||
        payrollLineAmountBySlug(
          lines,
          PAYROLL_CONCEPT_SLUG.FUEL_MILEAGE,
          Math.round(Number(fg.value.distance_in_km || 0) * Number(fg.value.fuel_cost_per_km || 0) * 100) / 100
        );
      sumItem += payrollLineAmountBySlug(lines, PAYROLL_CONCEPT_SLUG.ADDITIONAL_SALARY, Number(fg.value.additional_salary || 0));

      for (let i = 0; i < this.CONCEPTS_TO_SHOW; i++) {
        sumItem += Number(fg.controls[`plus_${i}_amount`].value || 0);
      }

      sumItem += payrollLineAmountBySlug(
        lines,
        PAYROLL_CONCEPT_SLUG.EXTRA_HOURS,
        estimateExtraHoursAmount(Number(fg.value.additional_hours || 0), Number(fg.value.brute_base_salary || 0))
      );

      sumItem *= 1 + Number(fg.value.ss_prcnt || 0) / 100;

      sum += sumItem;
    });

    this.total = sum;
  }

  buildSmCalculatedTooltip(formGroup: UntypedFormGroup): string {
    const currency = (value: number | null | undefined): string => this.currencyPipe.transform(value ?? 0) ?? '0';
    const lines: string[] = [
      `Base: ${currency(formGroup.value.brute_base_salary)}`,
      `Seniority: ${currency(formGroup.value.seniority_bonus)}`,
    ];

    const justification = formGroup.value.plus_justifications as PayrollPlusJustification | null;
    if (justification?.bisual_plus_original_amount != null && justification?.bisual_plus_amount_after_kpi != null) {
      lines.push(
        `Plus Bisual: ${currency(justification.bisual_plus_original_amount)} → ${currency(justification.bisual_plus_amount_after_kpi)}` +
          (justification.kpi_average_factor != null ? ` (${Math.round(justification.kpi_average_factor * 100)}%)` : '')
      );
    }

    for (let i = 0; i < this.CONCEPTS_TO_SHOW; i++) {
      const name: string = formGroup.value[`plus_${i}_name`] || `Plus ${i}`;
      const amount: number = formGroup.value[`plus_${i}_amount`] || 0;
      if (name || amount) {
        lines.push(`${name}: ${currency(amount)}`);
      }
    }

    return lines.join('\n');
  }

  private listenForm() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.isFormModified = true;
    });
  }
}
